import * as XLSX from 'xlsx'
import { notice } from '@/util/notice'
import i18n from '@/plugins/i18n'

export function changeRange(data) {
  data = JSON.parse(JSON.stringify(data)) // 解除參數的雙向綁定，並重設excel撈取的範圍
  data.page = 1
  data.size = 99999
  return data
}

// columns去掉不要的欄位
export function columnsHandler(columns, deleteArr) {
  const arr = columns.filter(
    (item) => !deleteArr.some((dItem) => dItem === item.dataIndex)
  )
  return arr
}

/**
 * 生成匯出Excel的資料(一組table)
 * @param {array} data  table資料
 * @param {array} columns  column欄位設定
 * @param {string} fileName  excel檔名
 * @param {boolean} handleBgColors  是否有背景色
 */
export function createExcelData(
  data,
  columns,
  fileName,
  { handleBgColors = false } = {}
) {
  const excelContents = data.map((item) => {
    const obj = {}
    columns.forEach((e) => {
      if (typeof e.getValue === 'function') {
        obj[e.title] = e.getValue({ record: item })
      } else {
        obj[e.title] = item[e.dataIndex]
      }
    })
    return obj
  })
  const excelData = {
    fileName: `${fileName}.xlsx`,
    content: [{ sheetName: fileName, contents: excelContents }],
  }
  this.excelFileOutput(excelData, handleBgColors)
}

// 生成匯出Excel的資料(多組table、column)
export function createExcelDataByMultiple(dataColumns, fileName) {
  const sheetContents = []
  dataColumns.forEach((dataColumn) => {
    const excelContents = dataColumn.data.map((item) => {
      const obj = {}
      dataColumn.column.forEach((e) => {
        if (typeof e.getValue === 'function') {
          obj[e.title] = e.getValue({ record: item })
        } else {
          obj[e.title] = item[e.dataIndex]
        }
      })
      return obj
    })
    sheetContents.push({
      contents: excelContents,
      sheetName: dataColumn.sheetName,
    })
  })
  const excelData = {
    fileName: `${fileName}.xlsx`,
    content: sheetContents,
  }
  this.excelFileOutput(excelData)
}

/**
 * 生成單一 CSV(以群組為單位堆疊：每組 = 摘要區塊 + 明細表，中間空行分隔)
 * @param {array} groups  陣列，每筆 { groupName, summaryRows, tableColumns, tableData }
 *   - groupName: 該組標題，會單獨佔一行放在該組摘要區塊之前
 *   - summaryRows: 二維陣列 [[label, value], ...]
 *   - tableColumns: 明細表欄位設定，格式同 createExcelData 的 columns（{title, dataIndex, getValue}）
 *   - tableData: 明細表資料陣列
 * @param {string} fileName  csv檔名
 */
export function createGroupedCsv(groups, fileName) {
  const rows = []
  groups.forEach(({ groupName, summaryRows, tableColumns, tableData }, index) => {
    if (index > 0) {
      rows.push([])
    }
    rows.push([groupName])
    rows.push(...summaryRows)
    rows.push([])
    rows.push(tableColumns.map((e) => e.title))
    tableData.forEach((item) => {
      rows.push(
        tableColumns.map((e) => {
          if (typeof e.getValue === 'function') {
            return e.getValue({ record: item })
          }
          return item[e.dataIndex]
        })
      )
    })
  })

  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, ws, 'export')
  XLSX.writeFile(wb, `${fileName}.csv`, { bookType: 'csv' })
}

// 匯出excel檔案
export function excelFileOutput(excelData, handleBgColors) {
  const totalContent = excelData.content
  const wb = XLSX.utils.book_new()
  const wbName = excelData.fileName

  for (const item in totalContent) {
    const sheetContent = XLSX.utils.json_to_sheet(totalContent[item].contents)
    const sheetName = totalContent[item].sheetName
    XLSX.utils.book_append_sheet(wb, sheetContent, sheetName)

    // 處理背景色
    if (handleBgColors) {
      const reg = /^#([0-9a-f]{3}){1,2}$/i

      Object.keys(sheetContent).forEach((key) => {
        if (reg.test(sheetContent[key].v)) {
          // value符合 色碼標準
          wb.Sheets[sheetName][key].s = {
            fill: {
              patternType: 'solid',
              fgColor: { rgb: sheetContent[key].v.replace('#', '') },
            },
          }
          sheetContent[key].v = ''
        }
      })
    }
  }

  XLSX.writeFile(wb, wbName)
}

/**
 * 上傳excel資料
 * @param {file} file  table資料
 * @param {boolean} isOneRow  是否為單行資料，excel有資料才會載入，空資料則不理會
 */
export async function handleBeforeUpload(file, isOneRow = true) {
  return new Promise((resolve, reject) => {
    // 檢查檔案類型是否為 Excel
    if (
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel'
    ) {
      const reader = new FileReader()

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

        const headers = jsonData[0]
        const rows = jsonData.slice(1)

        let formattedData = []
        rows.forEach((row) => {
          headers.reduce((obj, header, index) => {
            const rowData = row[index]
            if (isOneRow) {
              // 有資料才會載入，空資料則不理會
              if (rowData) {
                obj[header] = rowData
                formattedData.push(obj)
              }
            } else {
              obj[header] = rowData
              formattedData.push(obj)
            }
          }, {})
        })

        notice(
          'success',
          i18n.global.t('success_action', {
            input: i18n.global.t('uploadExcel'),
          })
        )
        resolve(formattedData)
      }

      reader.onerror = () => {
        notice(
          'error',
          i18n.global.t('error_action', { input: i18n.global.t('uploadExcel') })
        )
        reject(false)
      }

      reader.readAsArrayBuffer(file)
    } else {
      notice(
        'error',
        i18n.global.t('error_action', { input: i18n.global.t('uploadExcel') })
      )
      reject(false)
    }
  })
}
